import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { canInAnyScope, isSimilarKind } from '@testflow/shared';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type CatalogColumn, CatalogTable, columnWidth } from '../../components/catalog-table.js';
import { SearchEmptyNotice } from '../../components/search-empty-notice.js';
import { type TestDataRow, catalogApi } from '../../lib/catalog-api.js';
import { type DataKind, dataKindsApi, kindLabel } from '../../lib/data-kinds-api.js';
import { useScopedUser } from '../../lib/project-roles.js';
import { matchesQuery } from '../../lib/row-filter.js';
import { DataKindsPanel } from './data-kinds-panel.js';

// Design-Spec 3.5 Screen 3c: Testdaten-Tab. KAT-3: der Datenart-Filter refetcht serverseitig
// über `listTestData(p, kindId)` (Query-Key inkl. `kindFilter`), statt eine bereits geladene
// Liste clientseitig zu filtern.
//
// #47: Datenart ist jetzt eine eigene Entität (data_kind_id) statt eines freien Strings. Die
// Spalte ist ein `combobox`-Editor (Task 7): Auswahl EINER bestehenden Datenart ODER — bei
// project.configure — Inline-Anlage einer neuen. is_released und die Kontextspalten sind
// entfallen (#52-Review/CLAUDE.md: beide Felder hatten keine Konsequenz).

type Draft = {
  data_kind_id: string | null;
  data_kind_new: string | null;
  value: string;
  description: string | null;
};

const emptyDraft: Draft = {
  data_kind_id: null,
  data_kind_new: null,
  value: '',
  description: null,
};

// I4 (Review-Finding): Breiten getrennt exportiert, damit catalog-table.test.tsx
// computeTableMinWidth gegen die ECHTEN Breiten dieses Tabs pruefen kann. `description` hat
// bewusst KEINE eigene Breite (undefined) — die unbepreiste Spalte.
export const TESTDATA_COLUMN_WIDTHS: (string | undefined)[] = ['20%', '22%', undefined];

export function TestDataTab({ projectId, q }: { projectId: string; q?: string | undefined }) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useScopedUser(projectId);
  const canEdit = user ? canInAnyScope(user, 'catalog.edit') : false;
  const canConfigure = user ? canInAnyScope(user, 'project.configure') : false;
  const [kindFilter, setKindFilter] = useState('');
  const [managingKinds, setManagingKinds] = useState(false);

  const dataKindsQuery = useQuery({
    queryKey: ['data-kinds', projectId],
    queryFn: () => dataKindsApi.list(projectId),
  });
  const kinds = dataKindsQuery.data ?? [];
  const kindsById = useMemo(() => new Map(kinds.map((k) => [k.data_kind_id, k])), [kinds]);

  const options = useMemo(
    () =>
      kinds.map((k: DataKind) => ({ value: k.data_kind_id, label: kindLabel(k, i18n.language) })),
    [kinds, i18n.language],
  );

  const rowsQuery = useQuery({
    queryKey: ['testdata', projectId, kindFilter],
    queryFn: () => catalogApi.listTestData(projectId, kindFilter || undefined),
  });

  // #78: Freitextfilter ueber die bereits geladenen Zeilen (kein neuer Endpunkt) — zusaetzlich
  // zum serverseitigen Datenart-Filter oben (KAT-3). Die Datenart-Bezeichnung ist eine sichtbare
  // Spalte, deshalb zaehlt sie mit. matchesQuery (Task 3) buendelt die Filterregel.
  const trimmedQuery = (q ?? '').trim();
  const searching = trimmedQuery.length > 0;
  const rows = rowsQuery.data ?? [];
  const filteredRows = useMemo(() => {
    if (!searching) return rows;
    return rows.filter((r) => {
      const kind = kindsById.get(r.data_kind_id);
      const kindText = kind ? kindLabel(kind, i18n.language) : '';
      return matchesQuery(trimmedQuery, r.value, r.description, kindText);
    });
  }, [rows, searching, trimmedQuery, kindsById, i18n.language]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['testdata', projectId] });
    void queryClient.invalidateQueries({ queryKey: ['data-kinds', projectId] });
  };

  // Legt bei Bedarf zuerst die neue Datenart an und liefert deren id — sonst die bereits
  // gewaehlte data_kind_id direkt. Kein Try/Catch hier: schlaegt das Anlegen der Datenart fehl,
  // soll das ganz normal an CatalogTable durchschlagen (Zeile bleibt offen, Fehler sichtbar),
  // statt Testdaten mit einer nicht existierenden data_kind_id anzulegen.
  // Wichtig: invalidiere die data-kinds-Liste sofort nach dem Create (vor dem Testdaten-Write),
  // damit die neue Datenart in der Combobox sichtbar ist, falls das Testdaten-Anlegen fehlschlaegt.
  const resolveKindId = async (d: Draft): Promise<string> => {
    if (d.data_kind_new) {
      const created = await dataKindsApi.create(projectId, {
        label_de: d.data_kind_new,
        label_en: null,
      });
      void queryClient.invalidateQueries({ queryKey: ['data-kinds', projectId] });
      return created.data_kind_id;
    }
    if (!d.data_kind_id) throw new Error('data_kind_id required');
    return d.data_kind_id;
  };

  // errorToast: false auf den Testdaten-Mutationen — CatalogTable zeigt Anlegen-/Bearbeiten- und
  // Loesch-Fehler bereits namentlich in der Zeile (Spec Kapitel 2), der globale Toast waere hier
  // die doppelte, generische zweite Meldung (#52-Review Finding 1). Ein fehlgeschlagenes Anlegen
  // der Datenart selbst hat kein eigenes meta — der Fehler laeuft ganz normal ueber denselben
  // Promise-Reject an CatalogTable durch.
  const createRow = useMutation({
    mutationFn: async (d: Draft) => {
      const data_kind_id = await resolveKindId(d);
      return catalogApi.createTestData(projectId, {
        data_kind_id,
        value: d.value,
        description: d.description,
      });
    },
    meta: { successToast: 'feedback.created', errorToast: false },
    onSuccess: invalidate,
  });
  const updateRow = useMutation({
    mutationFn: async ({ id, d }: { id: string; d: Draft }) => {
      const data_kind_id = await resolveKindId(d);
      return catalogApi.updateTestData(projectId, id, {
        data_kind_id,
        value: d.value,
        description: d.description,
      });
    },
    meta: { errorToast: false },
    onSuccess: invalidate,
  });
  const deleteRow = useMutation({
    mutationFn: (id: string) => catalogApi.deleteTestData(projectId, id),
    meta: { successToast: 'feedback.deleted', errorToast: false },
    onSuccess: invalidate,
  });

  const columns: CatalogColumn<TestDataRow, Draft>[] = [
    {
      key: 'data_kind',
      header: t('catalog.fields.dataKind'),
      ...columnWidth(TESTDATA_COLUMN_WIDTHS[0]),
      render: (r) => {
        const kind = kindsById.get(r.data_kind_id);
        return kind ? kindLabel(kind, i18n.language) : '—';
      },
      editor: {
        kind: 'combobox',
        get: (d) => d.data_kind_id,
        set: (d, id) => ({ ...d, data_kind_id: id, data_kind_new: id ? null : d.data_kind_new }),
        setNew: (d, name) => ({ ...d, data_kind_new: name, data_kind_id: null }),
        getNew: (d) => d.data_kind_new,
        options,
        canCreate: canConfigure,
        similar: (typed) => options.find((o) => isSimilarKind(typed, o.label)) ?? null,
      },
    },
    {
      key: 'value',
      header: t('catalog.fields.value'),
      ...columnWidth(TESTDATA_COLUMN_WIDTHS[1]),
      render: (r) => r.value,
      editor: { kind: 'text', get: (d) => d.value, set: (d, v) => ({ ...d, value: v }) },
    },
    {
      key: 'description',
      header: t('catalog.fields.description'),
      render: (r) => r.description ?? '—',
      editor: {
        kind: 'textarea',
        get: (d) => d.description ?? '',
        set: (d, v) => ({ ...d, description: v || null }),
      },
    },
  ];

  return (
    <div className="h-full space-y-3 overflow-y-auto p-6">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <label className="text-xs text-text-muted" htmlFor="testdata-kind-filter">
            {t('catalog.fields.dataKind')}
          </label>
          <select
            id="testdata-kind-filter"
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value)}
            className="rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1 text-sm text-text-body"
          >
            <option value="">{t('catalog.testdata.allKinds')}</option>
            {options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        {canConfigure && (
          <button
            type="button"
            onClick={() => setManagingKinds(true)}
            className="rounded-btn border border-[var(--border)] px-2 py-1 text-xs text-text-body hover:border-brand-accent"
          >
            {t('catalog.dataKinds.manage')}
          </button>
        )}
      </div>

      {rowsQuery.isLoading ? (
        <div className="text-sm text-text-muted">{t('catalog.loading')}</div>
      ) : (
        <>
          {searching && filteredRows.length === 0 && <SearchEmptyNotice query={trimmedQuery} />}
          <CatalogTable
            columns={columns}
            rows={filteredRows}
            rowKey={(r) => r.test_data_id}
            toDraft={(r) => ({
              data_kind_id: r.data_kind_id,
              data_kind_new: null,
              value: r.value,
              description: r.description,
            })}
            emptyDraft={emptyDraft}
            onCreate={(d) => createRow.mutateAsync(d)}
            onUpdate={(id, d) => updateRow.mutateAsync({ id, d })}
            onDelete={(id) => deleteRow.mutateAsync(id)}
            deleteLabel={(r) => {
              const kind = kindsById.get(r.data_kind_id);
              return `${kind ? kindLabel(kind, i18n.language) : '—'} ${r.value}`;
            }}
            canEdit={canEdit}
            addLabel={t('catalog.actions.addTestData')}
          />
        </>
      )}

      {managingKinds && (
        <DataKindsPanel
          projectId={projectId}
          onClose={() => {
            setManagingKinds(false);
            void queryClient.invalidateQueries({ queryKey: ['data-kinds', projectId] });
          }}
        />
      )}
    </div>
  );
}
